import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { kanbanSliceSelectors } from "../../../redux/kanban/kanban.selector";
import { useTranslation } from "react-i18next";
import { WarningModal } from "../../../components/WarningModal";
import { AtiraToast } from "../../../utils/AtiraToast";
import { kanbanActions } from "../../../redux/kanban/kanban.slice";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { useFormContext } from "react-hook-form";
import { GetUserKanbanDto } from "../../../model/kanban/dto/GetUserDefaultKanbanDto";
import { TestIDs } from "../../../tests/types/testIds";

type Props = {
  open: boolean;
  onClose: () => void;
};

export const KanbanDeleteModal: React.FC<Props> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setValue } = useFormContext<GetUserKanbanDto>();

  const kanban = useAppSelector(kanbanSliceSelectors.selectUserCurrentKanban);
  const kanbansMeta = useAppSelector(kanbanSliceSelectors.selectKanbansMeta);
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const onDelete = async () => {
    try {
      setLoading(true);
      await dispatch(
        kanbanActions.deleteKanban({ userId, kanbanId: kanban?._id! }),
      ).unwrap();

      const newDefaultKanban = kanbansMeta?.find((k) => k._id !== kanban?._id);
      await dispatch(kanbanActions.getUserKanbansMeta({ userId })).unwrap();
      setValue("kanbanId", newDefaultKanban?._id || "");

      dispatch(
        kanbanActions.getUserKanban({
          userId,
          kanbanId: newDefaultKanban?._id!,
        }),
      );

      onClose();
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (!kanban) {
    return null;
  }

  return (
    <WarningModal
      isOpen={open}
      onClose={onClose}
      title={t("kanban.delete.modal.title", { name: kanban?.name })}
      description={t("kanban.delete.modal.description")}
      onConfirm={onDelete}
      confirmButtonText={t("common.delete")}
      loading={loading}
      confirmButtonDataTestId={TestIDs.kanbans.actions.deleteModalConfirm}
    />
  );
};
